import { ORDER_LIST_DEFAULT_PAGE_SIZE } from '@/fsd/pages/orders/lib/consts/orderListDefaultPageSize';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A custom hook to manage pagination ('page', 'pageSize') query parameters in the URL.
 * It provides the current page and pageSize, and a function to update them.
 *
 * @param {number} [defaultPageSize=ORDER_LIST_DEFAULT_PAGE_SIZE] - The default page size to use if not specified in the URL.
 * An object containing the current page, pageSize, and a function to set them.
 */

export interface IPaginationParams {
  page?: number;
  pageSize?: number;
}

interface IReturn {
  page: number;
  pageSize: number;
  setPagination: (params: IPaginationParams) => void;
}

export const usePaginationQueryParams = (
  defaultPageSize: number = ORDER_LIST_DEFAULT_PAGE_SIZE
): IReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || defaultPageSize;

  const setPagination = useCallback(
    ({
      page: newPage,
      pageSize: newPageSize,
    }: {
      page?: number;
      pageSize?: number;
    }) => {
      setSearchParams((prev) => {
        if (newPage !== undefined) {
          prev.set('page', String(newPage));
        }
        if (newPageSize !== undefined) {
          prev.set('pageSize', String(newPageSize));
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  return { page, pageSize, setPagination };
};
